/**
 * PinToggle — the one-click matter pin (a private watch), shared by the belt
 * rows (#471 Slice 1) and the case-detail panel header (#541).
 *
 * A pin watches the MATTER (lead), not a placement: one lead with two stalled
 * placements yields two belt rows sharing the leadId, so the same pin marks
 * both. Pin = create a watch task (no assignee, ever); un-pin = the existing
 * task `clear` action. Renders nothing when the caller has no lead (task /
 * firm-level rows, panels without a lead target).
 */

import { Pin } from "lucide-react";
import type { SurfacePin } from "@command-center/shared";
import { useCreateTask, useTaskAction } from "@/lib/use-surface-tasks";
import { cn } from "@/lib/utils";

export function PinToggle({
  leadId,
  pins,
  className,
  testId,
}: {
  /** The matter to watch; null renders nothing. */
  leadId: string | null;
  /** The viewer's current pins — pinned state + the task id to un-pin. */
  pins: SurfacePin[];
  className?: string;
  testId: string;
}) {
  const createPin = useCreateTask();
  const unpin = useTaskAction();
  if (leadId === null) return null;

  const pin = pins.find(
    (p) => p.task.about?.kind === "lead" && p.task.about.id === leadId,
  );
  const pending = createPin.isPending || unpin.isPending;
  const toggle = () => {
    if (pin) unpin.mutate({ id: pin.task.id, action: "clear" });
    else createPin.mutate({ kind: "watch", leadRecordId: leadId });
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={pending}
      aria-pressed={pin !== undefined}
      title={pin !== undefined ? "Un-pin this matter" : "Pin this matter"}
      data-testid={testId}
      data-pinned={pin !== undefined ? "true" : "false"}
      className={cn(
        "rounded p-1 transition-colors disabled:opacity-50",
        pin !== undefined
          ? "text-foreground"
          : "text-muted-foreground hover:text-foreground",
        className,
      )}
    >
      <Pin className={cn("h-4 w-4", pin !== undefined && "fill-current")} />
    </button>
  );
}
